import axios from "axios";
import { toast } from "sonner";
import { Trash2, Download, FileText, Image as ImageIcon, Music, Film, Loader2, AlertTriangle } from "lucide-react";
import { API } from "../../context/AuthContext";

const KIND_ICON = { pdf: FileText, image: ImageIcon, audio: Music, video: Film };

function fmtSize(bytes) {
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

export default function FilesList({ files, onDeleted }) {
  const del = async (id) => {
    try {
      await axios.delete(`${API}/files/${id}`);
      onDeleted(id);
      toast.success("File erased from the archive.");
    } catch {
      toast.error("Couldn't delete that file.");
    }
  };

  const download = async (f) => {
    try {
      const res = await axios.get(`${API}/files/${f.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.stored_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download that file.");
    }
  };

  if (files.length === 0) return null;

  return (
    <div className="bg-[#131f3a] border border-white/5 p-5 rounded-2xl shadow-xl space-y-3">
      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-300">Chrono File Archive</h4>
      <div className="space-y-2">
        {files.map((f) => {
          const Icon = KIND_ICON[f.kind] || FileText;
          return (
            <div key={f.file_id} className="flex items-center gap-3 bg-gray-950 border border-white/5 rounded-xl px-3 py-2.5" data-testid={`file-row-${f.file_id}`}>
              <Icon className="w-4 h-4 text-[#2dd4bf] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-200 font-mono truncate" title={f.stored_name}>{f.stored_name}</div>
                <div className="text-[10px] text-gray-400 font-mono flex items-center gap-2">
                  {fmtSize(f.size)}
                  {f.status === "processing" && (
                    <span className="flex items-center gap-1 text-[#f59e0b]" data-testid={`file-status-processing-${f.file_id}`}>
                      <Loader2 className="w-2.5 h-2.5 animate-spin" /> Flux is analyzing...
                    </span>
                  )}
                  {f.status === "done" && <span className="text-emerald-400">{f.event_count} event{f.event_count === 1 ? "" : "s"} extracted</span>}
                  {f.status === "failed" && (
                    <span className="flex items-center gap-1 text-rose-400" title={f.error}>
                      <AlertTriangle className="w-2.5 h-2.5" /> failed
                    </span>
                  )}
                </div>
              </div>
              {f.status === "done" && (
                <button
                  onClick={() => download(f)}
                  data-testid={`file-download-${f.file_id}`}
                  className="text-gray-400 hover:text-[#f59e0b] transition-colors duration-200"
                  title="Download chronologically renamed file"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={() => del(f.file_id)} data-testid={`file-delete-${f.file_id}`} className="text-gray-500 hover:text-rose-400 transition-colors duration-200">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
